import { format } from "date-fns";
import { de } from "date-fns/locale";

type Platform = "linkedin" | "facebook" | "instagram" | "google_business";

export interface TimeSlot {
  label: string;
  date: Date;
  isPeak: boolean;
  isTaken: boolean;
}

// Optimal posting times for DACH market (hours in Europe/Berlin local time)
// Days: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
const PLATFORM_SCHEDULE: Record<Platform, { days: number[]; peakDay: number; hours: number[]; peakHour: number }> = {
  linkedin: {
    days: [2, 3, 4],       // Tue-Thu
    peakDay: 3,            // Wed
    hours: [11, 13, 15, 16, 17],
    peakHour: 16,
  },
  facebook: {
    days: [2, 3, 4],
    peakDay: 3,
    hours: [9, 10, 12, 13],
    peakHour: 10,
  },
  instagram: {
    days: [2, 3, 4],
    peakDay: 3,
    hours: [11, 12, 13, 19, 20],
    peakHour: 12,
  },
  google_business: {
    days: [2, 3, 4],
    peakDay: 3,
    hours: [9, 10, 12],
    peakHour: 10,
  },
};

// Tolerance window: two posts within 30 minutes of each other count as a conflict
const CONFLICT_WINDOW_MS = 30 * 60 * 1000;

// Convert a Date to Europe/Berlin local components
function toBerlinDate(date: Date): { year: number; month: number; day: number; hours: number; minutes: number; dayOfWeek: number } {
  const berlinStr = date.toLocaleString("en-US", { timeZone: "Europe/Berlin" });
  const berlinDate = new Date(berlinStr);
  return {
    year: berlinDate.getFullYear(),
    month: berlinDate.getMonth(),
    day: berlinDate.getDate(),
    hours: berlinDate.getHours(),
    minutes: berlinDate.getMinutes(),
    dayOfWeek: berlinDate.getDay(),
  };
}

// Create a Date object for a specific Europe/Berlin local time
function createBerlinDate(year: number, month: number, day: number, hour: number): Date {
  const pad = (n: number) => String(n).padStart(2, "0");
  const dateStr = `${year}-${pad(month + 1)}-${pad(day)}T${pad(hour)}:00:00`;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    timeZoneName: "shortOffset",
  });
  const rough = new Date(`${dateStr}Z`);
  const parts = formatter.formatToParts(rough);
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  let offsetHours = 1; // CET default
  if (tzPart?.value) {
    const match = tzPart.value.match(/GMT([+-]\d+)/);
    if (match) offsetHours = parseInt(match[1], 10);
  }

  return new Date(`${dateStr}+${pad(offsetHours)}:00`);
}

/**
 * Check if a candidate time conflicts with any existing scheduled time.
 * Two posts within CONFLICT_WINDOW_MS of each other on the same platform = conflict.
 */
function isSlotTaken(candidate: Date, existingTimes: Date[]): boolean {
  return existingTimes.some(
    (existing) => Math.abs(candidate.getTime() - existing.getTime()) < CONFLICT_WINDOW_MS,
  );
}

/**
 * Get the next 5 optimal posting time slots for a platform.
 * Skips times in the past, weekends, and marks slots that conflict with existing scheduled posts.
 *
 * @param platform - Target social platform
 * @param existingScheduledTimes - Array of already-scheduled Date objects for this platform
 * @param fromDate - Start scanning from this date (defaults to now)
 */
export function getSuggestedTimes(
  platform: Platform,
  existingScheduledTimes: Date[] = [],
  fromDate?: Date,
): TimeSlot[] {
  const schedule = PLATFORM_SCHEDULE[platform] || PLATFORM_SCHEDULE.linkedin;
  const now = fromDate || new Date();
  const berlin = toBerlinDate(now);
  const slots: TimeSlot[] = [];

  // Scan up to 14 days ahead to find enough available slots
  // We collect up to 5 *available* slots, but also mark taken ones
  let availableCount = 0;
  for (let dayOffset = 0; dayOffset < 14 && availableCount < 5; dayOffset++) {
    const targetDate = new Date(berlin.year, berlin.month, berlin.day + dayOffset);
    const targetYear = targetDate.getFullYear();
    const targetMonth = targetDate.getMonth();
    const targetDay = targetDate.getDate();
    const targetDayOfWeek = targetDate.getDay();

    // Skip weekends
    if (targetDayOfWeek === 0 || targetDayOfWeek === 6) continue;

    // Check if this weekday is in the platform's optimal days
    if (!schedule.days.includes(targetDayOfWeek)) continue;

    for (const hour of schedule.hours) {
      if (availableCount >= 5) break;

      const slotDate = createBerlinDate(targetYear, targetMonth, targetDay, hour);

      // Skip times in the past (with 15min buffer)
      if (slotDate.getTime() <= now.getTime() + 15 * 60 * 1000) continue;

      const isPeak = targetDayOfWeek === schedule.peakDay && hour === schedule.peakHour;
      const isTaken = isSlotTaken(slotDate, existingScheduledTimes);

      if (!isTaken) {
        availableCount++;
      }

      slots.push({
        label: format(slotDate, "EEE, d. MMM · HH:mm", { locale: de }),
        date: slotDate,
        isPeak,
        isTaken,
      });
    }
  }

  return slots;
}

/**
 * Check if a custom time conflicts with existing scheduled posts.
 */
export function hasConflict(candidateTime: Date, existingScheduledTimes: Date[]): boolean {
  return isSlotTaken(candidateTime, existingScheduledTimes);
}
